import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useUserManagement } from '../context/UserManagementContext';
import { userManagementService, User } from '../services/userManagementService';

interface UserGridProps {
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  onDisableUser: (user: User) => void;
  onEnableUser: (user: User) => void;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (limit: number) => void;
  loading?: boolean;
  error?: string | null;
}

export function UserGrid({
  onEditUser,
  onDeleteUser,
  onDisableUser,
  onEnableUser,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  error = null,
}: UserGridProps) {
  const {
    state,
    selectUser,
    selectAllUsers,
    clearSelection,
    isUserSelected,
    isAllSelected,
  } = useUserManagement();

  const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    setAnchorEl(prev => ({ ...prev, [userId]: event.currentTarget }));
  };

  const handleMenuClose = (userId: string) => {
    setAnchorEl(prev => ({ ...prev, [userId]: null }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    // MUI uses 0-based indexing, but our API uses 1-based
    if (onPageChange) {
      onPageChange(newPage + 1);
    }
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(newLimit);
    }
  };

  const getRoleColor = (role: 'PATIENT' | 'CAREGIVER' | 'ADMIN') => {
    switch (role) {
      case 'PATIENT':
        return 'primary';
      case 'CAREGIVER':
        return 'warning';
      case 'ADMIN':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: 'PATIENT' | 'CAREGIVER' | 'ADMIN') => {
    switch (role) {
      case 'PATIENT':
        return 'Patient';
      case 'CAREGIVER':
        return 'Caregiver';
      case 'ADMIN':
        return 'Admin';
      default:
        return 'Unknown';
    }
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    if (user.lastName) {
      return user.lastName;
    }
    return user.email;
  };

  const getUserInitials = (user: User) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return user.email.charAt(0).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={state.selectedUsers.length > 0 && state.selectedUsers.length < state.users.length}
                  checked={isAllSelected()}
                  onChange={selectAllUsers}
                />
              </TableCell>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Stack alignItems="center" spacing={2}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    <Typography variant="h6" color="text.secondary">
                      No users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {state.searchQuery ? 'Try adjusting your search criteria' : 'No users have been created yet'}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              state.users.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  selected={isUserSelected(user.id)}
                  sx={{
                    opacity: user.disabled ? 0.6 : 1,
                    '&:hover': {
                      backgroundColor: user.disabled ? 'action.hover' : 'action.hover',
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isUserSelected(user.id)}
                      onChange={() => selectUser(user.id)}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={user.avatar || undefined}
                        sx={{
                          bgcolor: userManagementService.getRoleColor(user.role),
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getUserInitials(user)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {getUserDisplayName(user)}
                        </Typography>
                        {user.disabled && (
                          <Chip
                            label="Disabled"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleColor(user.role) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={user.disabled ? 'Disabled' : 'Active'}
                      color={user.disabled ? 'default' : 'success'}
                      size="small"
                      variant="outlined"
                      icon={user.disabled ? <BlockIcon /> : <CheckCircleIcon />}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(user.createdAt)}
                    </Typography>
                  </TableCell>
                  
                  <TableCell align="right">
                    <Tooltip title="More actions">
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, user.id)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    
                    <Menu
                      anchorEl={anchorEl[user.id]}
                      open={Boolean(anchorEl[user.id])}
                      onClose={() => handleMenuClose(user.id)}
                    >
                      <MenuItem onClick={() => {
                        onEditUser(user);
                        handleMenuClose(user.id);
                      }}>
                        <ListItemIcon>
                          <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                      </MenuItem>
                      
                      {user.disabled ? (
                        <MenuItem onClick={() => {
                          onEnableUser(user);
                          handleMenuClose(user.id);
                        }}>
                          <ListItemIcon>
                            <CheckCircleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Enable</ListItemText>
                        </MenuItem>
                      ) : (
                        <MenuItem onClick={() => {
                          onDisableUser(user);
                          handleMenuClose(user.id);
                        }}>
                          <ListItemIcon>
                            <BlockIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Disable</ListItemText>
                        </MenuItem>
                      )}
                      
                      <MenuItem 
                        onClick={() => {
                          onDeleteUser(user);
                          handleMenuClose(user.id);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon>
                          <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {state.pagination && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={state.pagination.total}
          rowsPerPage={state.pagination.limit}
          page={state.pagination.page - 1} // MUI uses 0-based indexing
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Users per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      )}
      
      {state.selectedUsers.length > 0 && (
        <Box p={2} bgcolor="primary.light" color="primary.contrastText">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">
              {state.selectedUsers.length} user(s) selected
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={clearSelection}
              >
                Clear Selection
              </Button>
              {/* Bulk actions will be added here */}
            </Stack>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
